import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { customerIdAtom } from "~/AtomWithDynamoDB";
import { api } from "~/api";

export type InputMethod = "file" | "text";

export interface AwsKnowledgeBase {
  kb_id: string;
  display_name: string;
  kb_identifier: string;
  s3_suffix: string;
  bedrock_kb_id: string;
  ds_id: string;
}

export type SyncState = "idle" | "starting" | "in_progress" | "complete" | "failed" | "stopped";

export interface KbSyncStatus {
  state: SyncState;
  jobId: string | null;
}

export interface AwsKnowledgeFile {
  key: string;
  name: string;
  size: number;
  lastModified: string | Date;
}

/**
 * AWS Bedrock/S3ナレッジセクションのカスタムフック
 */
export const useAwsKnowledgeSection = () => {
  const { t } = useTranslation();
  const customerId = useAtomValue(customerIdAtom) ?? "";

  const [knowledgeBases, setKnowledgeBases] = useState<AwsKnowledgeBase[]>([]);
  const [isKbLoading, setIsKbLoading] = useState(true);
  const [addingKbId, setAddingKbId] = useState("");
  const [inputMethod, setInputMethod] = useState<InputMethod>("file");
  const [newName, setNewName] = useState("");
  const [newText, setNewText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filesByKb, setFilesByKb] = useState<Record<string, AwsKnowledgeFile[]>>({});
  // キー: bedrock_kb_id（データソース単位で1つ）
  const [syncByBedrockKb, setSyncByBedrockKb] = useState<Record<string, KbSyncStatus>>({});
  // キー: bedrock_kb_id → (s3Key → DocumentStatus)
  const [docStatusByKb, setDocStatusByKb] = useState<Record<string, Record<string, string>>>({});
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [alertModal, setAlertModal] = useState<{ isOpen: boolean; message: string }>({
    isOpen: false,
    message: "",
  });

  // 同期ボタン用: bedrock_kb_idでユニーク化したKBリスト
  const uniqueBedrockKbs = useMemo(() => {
    const seen = new Set<string>();
    return knowledgeBases.filter((kb) => {
      if (!kb.bedrock_kb_id || seen.has(kb.bedrock_kb_id)) return false;
      seen.add(kb.bedrock_kb_id);
      return true;
    });
  }, [knowledgeBases]);

  // 現在選択中のKBのS3プレフィックスを取得
  const currentKb = knowledgeBases.find((kb) => kb.kb_id === addingKbId);
  const currentPrefix = currentKb ? `${currentKb.kb_identifier}/${currentKb.s3_suffix}/` : "";

  /**
   * ナレッジベース一覧をDynamoDBから取得
   */
  const fetchKnowledgeBases = useCallback(async () => {
    if (!customerId) return;
    setIsKbLoading(true);
    try {
      // customer_id経由でknowledge_bases→custom_knowledgesを一括取得
      const items = await api.knowledge.customListByCustomer.query({ customer_id: customerId });
      setKnowledgeBases(
        items.map((item) => ({
          kb_id: item.s3_suffix,
          display_name: item.display_name,
          kb_identifier: item.kb_identifier,
          s3_suffix: item.s3_suffix,
          bedrock_kb_id: item.bedrock_kb_id,
          ds_id: item.ds_id,
        }))
      );
    } catch (error) {
      console.error("[KnowledgeSection] ナレッジベース一覧取得エラー:", error);
    } finally {
      setIsKbLoading(false);
    }
  }, [customerId]);

  /**
   * 指定KBのS3ファイル一覧を取得
   */
  const fetchFiles = useCallback(
    async (kb_id: string, prefix: string): Promise<AwsKnowledgeFile[]> => {
      try {
        const res = await api.knowledge.list.query({ prefix });
        const files = res.files as unknown as AwsKnowledgeFile[];
        setFilesByKb((prev) => ({ ...prev, [kb_id]: files }));
        return files;
      } catch (error) {
        console.error("ファイル一覧取得エラー:", error);
        return [];
      }
    },
    []
  );

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  /**
   * ファイル検証
   */
  const validateFile = (file: File): boolean => {
    const maxSize = 15 * 1024 * 1024; // 15MB
    if (file.size > maxSize) {
      setAlertModal({ isOpen: true, message: t("fileSizeExceeded") });
      return false;
    }
    const supportedExtensions = [
      ".txt",
      ".md",
      ".pdf",
      ".html",
      ".xlsx",
      ".xls",
      ".docx",
      ".csv",
      ".htm",
    ];
    const isSupported = supportedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
    if (!isSupported) {
      setAlertModal({ isOpen: true, message: t("unsupportedFileFormat") });
      return false;
    }
    return true;
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoading) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (isLoading) return;
    const file = e.dataTransfer.files[0];
    if (file && validateFile(file)) setSelectedFile(file);
  };

  const handleCancelClick = () => {
    setNewName("");
    setNewText("");
    setSelectedFile(null);
    setInputMethod("file");
    setAddingKbId("");
    setIsDragOver(false);
  };

  const validateRequired = (): boolean => {
    if (!addingKbId || !newName.trim()) return false;
    if (inputMethod === "text") return !!newText.trim();
    if (inputMethod === "file") return !!selectedFile;
    return false;
  };

  /**
   * ファイルをBase64に変換
   */
  const convertFileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /**
   * アップロード処理（内部ヘルパー）
   */
  const uploadKnowledge = async (): Promise<void> => {
    if (inputMethod === "file" && selectedFile) {
      const base64 = await convertFileToBase64(selectedFile);
      const ext = selectedFile.name.substring(selectedFile.name.lastIndexOf(".") + 1);
      await api.knowledge.uploadFile.mutate({
        prefix: currentPrefix,
        name: newName.trim(),
        extension: ext,
        base64,
        contentType: selectedFile.type || "application/octet-stream",
        category: "custom",
      });
    } else if (inputMethod === "text") {
      await api.knowledge.uploadText.mutate({
        prefix: currentPrefix,
        name: newName.trim(),
        text: newText,
        category: "custom",
      });
    }
  };

  /**
   * 保存処理（S3アップロード）
   */
  const handleSave = async (): Promise<void> => {
    if (!validateRequired()) {
      setAlertModal({ isOpen: true, message: t("pleaseEnterAllFields") });
      return;
    }
    // 同一ナレッジベース内の登録名重複チェック
    const existingFiles = filesByKb[addingKbId] ?? [];
    const trimmedName = newName.trim();
    const isDuplicate = existingFiles.some((file) => {
      const baseName = file.name.includes(".")
        ? file.name.substring(0, file.name.lastIndexOf("."))
        : file.name;
      return baseName === trimmedName;
    });
    if (isDuplicate) {
      setAlertModal({ isOpen: true, message: t("nameAlreadyExists") });
      return;
    }
    setIsLoading(true);
    try {
      await uploadKnowledge();
      setIsSuccessModalOpen(true);
      handleCancelClick();
      const updatedFiles = await fetchFiles(addingKbId, currentPrefix);
      // ファイル追加後は同期状態を再取得し、新規アップロードファイルのステータスを削除して同期ボタンをidleに戻す
      if (currentKb?.bedrock_kb_id) {
        await fetchDocStatus(currentKb);
        // 新規アップロードしたファイルはBedrockに古いINDEXEDが残るためステータスを削除
        const trimmedName = newName.trim();
        const newFile = updatedFiles.find((f) => {
          const baseName = f.name.includes(".")
            ? f.name.substring(0, f.name.lastIndexOf("."))
            : f.name;
          return baseName === trimmedName;
        });
        if (newFile) {
          setDocStatusByKb((prev) => {
            const kbStatus = { ...(prev[currentKb.bedrock_kb_id] ?? {}) };
            delete kbStatus[newFile.key];
            return { ...prev, [currentKb.bedrock_kb_id]: kbStatus };
          });
        }
        setSyncByBedrockKb((prev) => ({
          ...prev,
          [currentKb.bedrock_kb_id]: { state: "idle", jobId: null },
        }));
      }
    } catch (error) {
      console.error("アップロードエラー:", error);
      const message = error instanceof Error ? error.message : String(error);
      setAlertModal({ isOpen: true, message: `${t("errorAddingData")}: ${message}` });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Bedrock KBのドキュメント単位のインデックスステータスを取得する
   */
  const fetchDocStatus = useCallback(async (kb: AwsKnowledgeBase): Promise<void> => {
    if (!kb.bedrock_kb_id || !kb.ds_id) return;
    try {
      const docs = await api.knowledge.docStatus.query({
        knowledgeBaseId: kb.bedrock_kb_id,
        dataSourceId: kb.ds_id,
      });
      const statusMap: Record<string, string> = {};
      for (const doc of docs) {
        statusMap[doc.s3Key] = doc.status;
      }
      setDocStatusByKb((prev) => ({ ...prev, [kb.bedrock_kb_id]: statusMap }));
    } catch (error) {
      console.error("[KnowledgeSection] ドキュメントステータス取得エラー:", error);
    }
  }, []);

  // docStatusByKb・knowledgeBasesの最新値をrefで保持（定期更新クロージャ用）
  const docStatusByKbRef = useRef(docStatusByKb);
  docStatusByKbRef.current = docStatusByKb;
  const knowledgeBasesRef = useRef(knowledgeBases);
  knowledgeBasesRef.current = knowledgeBases;

  // KB一覧ロード完了後、初回訪問時に全KBの同期状態を取得する
  useEffect(() => {
    if (isKbLoading || uniqueBedrockKbs.length === 0) return;
    for (const kb of uniqueBedrockKbs) {
      fetchDocStatus(kb);
    }
  }, [isKbLoading, uniqueBedrockKbs, fetchDocStatus]);

  // 同期済みバッジを60秒ごとに自動更新
  useEffect(() => {
    const interval = setInterval(() => {
      const trackedIds = Object.keys(docStatusByKbRef.current);
      for (const bedrock_kb_id of trackedIds) {
        const kb = knowledgeBasesRef.current.find((k) => k.bedrock_kb_id === bedrock_kb_id);
        if (kb) fetchDocStatus(kb);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchDocStatus]);

  /**
   * Bedrock KBの同期（差分取り込み）を開始し、完了までポーリングする
   */
  const startSync = useCallback(
    async (kb: AwsKnowledgeBase): Promise<void> => {
      const { bedrock_kb_id, ds_id } = kb;
      if (!bedrock_kb_id || !ds_id) {
        setAlertModal({ isOpen: true, message: t("syncFailed") });
        return;
      }
      setSyncByBedrockKb((prev) => ({
        ...prev,
        [bedrock_kb_id]: { state: "starting", jobId: null },
      }));
      let jobId = "";
      try {
        const res = await api.knowledge.startSync.mutate({
          knowledgeBaseId: bedrock_kb_id,
          dataSourceId: ds_id,
        });
        jobId = res.jobId;
        setSyncByBedrockKb((prev) => ({
          ...prev,
          [bedrock_kb_id]: { state: "in_progress", jobId },
        }));
      } catch (error) {
        console.error("同期開始エラー:", error);
        const message = error instanceof Error ? error.message : String(error);
        setSyncByBedrockKb((prev) => ({
          ...prev,
          [bedrock_kb_id]: { state: "failed", jobId: null },
        }));
        setAlertModal({ isOpen: true, message: `${t("syncFailed")}: ${message}` });
        return;
      }
      // ポーリング（最大20回・5秒間隔）
      let attempts = 0;
      const poll = async (): Promise<void> => {
        attempts++;
        try {
          const status = await api.knowledge.syncStatus.query({
            knowledgeBaseId: bedrock_kb_id,
            dataSourceId: ds_id,
            jobId,
          });
          const raw = status.status as string;
          if (raw === "IN_PROGRESS" || raw === "STARTING") {
            setSyncByBedrockKb((prev) => ({
              ...prev,
              [bedrock_kb_id]: { state: "in_progress", jobId },
            }));
            if (attempts < 20) setTimeout(poll, 5000);
          } else if (raw === "COMPLETE") {
            setSyncByBedrockKb((prev) => ({
              ...prev,
              [bedrock_kb_id]: { state: "complete", jobId },
            }));
            // 完了後にドキュメントステータスを再取得してバッジを即時更新
            await fetchDocStatus(kb);
          } else {
            setSyncByBedrockKb((prev) => ({
              ...prev,
              [bedrock_kb_id]: { state: "failed", jobId },
            }));
          }
        } catch (error) {
          console.error("同期状態確認エラー:", error);
          setSyncByBedrockKb((prev) => ({ ...prev, [bedrock_kb_id]: { state: "failed", jobId } }));
        }
      };
      await poll();
    },
    [t, fetchDocStatus]
  );

  /**
   * ファイル削除
   */
  const handleDeleteFile = async (kb_id: string, key: string, prefix: string) => {
    setIsLoading(true);
    try {
      await api.knowledge.delete.mutate({ key });
      setAlertModal({
        isOpen: true,
        message: t("documentDeletionCompleted") + "\n" + t("syncToReflectKnowledge"),
      });
      await fetchFiles(kb_id, prefix);
      // ファイル削除後は同期状態を再取得し、同期ボタンをidleに戻す
      const deletedKb = knowledgeBases.find((k) => k.kb_id === kb_id);
      if (deletedKb?.bedrock_kb_id) {
        await fetchDocStatus(deletedKb);
        setSyncByBedrockKb((prev) => ({
          ...prev,
          [deletedKb.bedrock_kb_id]: { state: "idle", jobId: null },
        }));
      }
    } catch (error) {
      console.error("削除エラー:", error);
      setAlertModal({ isOpen: true, message: t("errorDuringDeletion") });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    knowledgeBases,
    isKbLoading,
    filesByKb,
    fetchFiles,
    addingKbId,
    setAddingKbId,
    inputMethod,
    setInputMethod,
    newName,
    setNewName,
    newText,
    setNewText,
    selectedFile,
    setSelectedFile,
    isDragOver,
    isLoading,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleCancelClick,
    validateFile,
    handleSave,
    handleDeleteFile,
    syncByBedrockKb,
    uniqueBedrockKbs,
    startSync,
    docStatusByKb,
    fetchDocStatus,
    isSuccessModalOpen,
    closeSuccessModal: () => setIsSuccessModalOpen(false),
    alertModal,
    closeAlertModal: () => setAlertModal({ isOpen: false, message: "" }),
  };
};
